/**
 * Guards around the "sibling fan-out" relay — the client-side re-publish of a
 * terminal signal (END/REJECT/BUSY/ACCEPT) addressed to the sender's OWN user
 * id so their other signed-in devices stop ringing.
 *
 * The AppSync onVoiceCallSignal subscription deliberately broadcasts every
 * published signal to ALL devices of BOTH call parties (its filter matches
 * callerId/calleeId, not recipientUserId), so a sibling relay also reaches the
 * OTHER party's devices. Before 2026-08-17 each of those devices re-relayed
 * the relay, creating an infinite cross-user END ping-pong (~4 signals/sec
 * sustained for minutes after every call) that hammered the signalling lambda
 * and churned the iOS CallKit teardown path on every hop.
 */

/**
 * True when a received signal is itself a sibling fan-out relay: a device
 * addressing its OWN user (recipientUserId === senderId). Original
 * cross-party signals always address the peer. Relays must NEVER be
 * re-relayed.
 */
export function isSiblingRelaySignal(signal: {
  senderId: string
  recipientUserId?: string | null
}): boolean {
  return Boolean(signal.recipientUserId) && signal.recipientUserId === signal.senderId
}

/**
 * Whether a terminal received from the PEER is re-published to our own
 * devices. Never for END: the peer's END already reaches every one of our
 * devices (the subscription delivers to all devices of both parties), so each
 * client — idle web tabs included — re-sending it after a remote END was pure
 * duplicate traffic through the signalling lambda (2026-10-19).
 */
export function shouldRelayPeerTerminal(type: string): boolean {
  return type !== 'END'
}

const MAX_TRACKED_RELAYS = 200

/** Relays already sent by this device — one per callId+type, ever. */
const sentRelayKeys = new Set<string>()

/**
 * Belt to the {@link isSiblingRelaySignal} suspenders: whatever the trigger,
 * a device relays a given signal type for a given call at most ONCE. Returns
 * whether this relay may proceed (and records it as sent when it may).
 */
export function claimSiblingRelayOnce(callId: string, type: string): boolean {
  const key = `${callId}:${type}`
  if (sentRelayKeys.has(key)) return false
  sentRelayKeys.add(key)
  if (sentRelayKeys.size > MAX_TRACKED_RELAYS) {
    const oldest = sentRelayKeys.values().next().value
    if (oldest) sentRelayKeys.delete(oldest)
  }
  return true
}

/** Test hook: forget all recorded relays. */
export function resetSiblingRelayTracking(): void {
  sentRelayKeys.clear()
}
